import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, Palette } from 'lucide-react';

const ColorMatchGame = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [level, setLevel] = useState(1);
  const [targetColor, setTargetColor] = useState('');
  const [colorOptions, setColorOptions] = useState([]);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);

  const colors = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#FFA500', '#800080', '#008000', '#FFC0CB', '#A52A2A', '#000080'
  ];

  const colorNames = {
    '#FF0000': 'Red',
    '#00FF00': 'Green',
    '#0000FF': 'Blue',
    '#FFFF00': 'Yellow',
    '#FF00FF': 'Magenta',
    '#00FFFF': 'Cyan',
    '#FFA500': 'Orange',
    '#800080': 'Purple',
    '#008000': 'Dark Green',
    '#FFC0CB': 'Pink',
    '#A52A2A': 'Brown',
    '#000080': 'Navy'
  };

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setCorrectAnswers(0);
    setTotalQuestions(0);
    generateNewQuestion();
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setCorrectAnswers(0);
    setTotalQuestions(0);
    setTargetColor('');
    setColorOptions([]);
  };

  const generateNewQuestion = () => {
    const availableColors = [...colors];
    const target = availableColors[Math.floor(Math.random() * availableColors.length)];
    setTargetColor(target);
    
    // Generate options (including the correct one)
    const options = [target];
    availableColors.splice(availableColors.indexOf(target), 1);
    
    // Add wrong options
    while (options.length < 4) {
      const randomColor = availableColors[Math.floor(Math.random() * availableColors.length)];
      options.push(randomColor);
      availableColors.splice(availableColors.indexOf(randomColor), 1);
    }
    
    // Shuffle options
    setColorOptions(options.sort(() => Math.random() - 0.5));
    setTotalQuestions(prev => prev + 1);
  };

  const handleColorClick = (selectedColor) => {
    if (!isPlaying || gameOver) return;

    if (selectedColor === targetColor) {
      setScore(prev => prev + 10 * level);
      setCorrectAnswers(prev => prev + 1);
      
      // Level up every 5 correct answers
      if ((correctAnswers + 1) % 5 === 0) {
        setLevel(prev => prev + 1);
      }
    } else {
      setScore(prev => Math.max(0, prev - 5));
    }

    // Generate next question after a short delay
    setTimeout(() => {
      if (isPlaying) {
        generateNewQuestion();
      }
    }, 500);
  };

  useEffect(() => {
    let interval;
    if (isPlaying && !gameOver) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameOver]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const accuracy = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Palette className="h-6 w-6 text-neon-purple" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-purple">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !gameOver && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Color Match Challenge
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Match the color name with the correct color swatch. Be quick and accurate!
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Game Area */}
      {isPlaying && !gameOver && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center mb-6">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              What color is this?
            </h4>
            
            {/* Target Color Display */}
            <div className="flex justify-center mb-6">
              <div
                className="w-32 h-32 rounded-lg border-4 border-white shadow-lg"
                style={{ backgroundColor: targetColor }}
              />
            </div>
            
            {/* Color Options */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {colorOptions.map((color, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleColorClick(color)}
                  className="cyber-button p-4 text-center hover:scale-105 transition-transform duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="text-sm font-cyber font-bold text-white">
                    {colorNames[color]}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Game Stats */}
      {isPlaying && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Game Statistics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-gray-400 font-tech">Accuracy</p>
                <p className="text-2xl font-cyber font-bold text-neon-blue">{accuracy}%</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Correct</p>
                <p className="text-2xl font-cyber font-bold text-neon-green">{correctAnswers}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Questions</p>
                <p className="text-2xl font-cyber font-bold text-neon-pink">{totalQuestions}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-red/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-red mb-4">
            Game Over!
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Final Score: {score} | Accuracy: {accuracy}% | Time: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={startGame}
              className="cyber-button text-lg px-6 py-3"
            >
              Play Again
            </button>
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3 border-neon-red text-neon-red hover:bg-neon-red hover:text-dark-300"
            >
              Reset
            </button>
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      <div className="cyber-card p-6">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            How to Play
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue mb-2">Objective</p>
              <p>Match the color name with the displayed color swatch</p>
            </div>
            <div>
              <p className="font-bold text-neon-green mb-2">Scoring</p>
              <p>+10 points per correct answer, -5 points per wrong answer</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink mb-2">Level Up</p>
              <p>Level up every 5 correct answers</p>
            </div>
            <div>
              <p className="font-bold text-neon-yellow mb-2">Strategy</p>
              <p>Be quick but accurate to maximize your score!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorMatchGame;
